import { endpoints } from "@/configs/Settings";

import {
  httpDelete,
  httpGet,
  httpPost,
} from "src-next/shared/api/httpClient";

import type { CreateMessageTemplateInput, MessageTemplate } from "../types";
import {
  buildWhatsAppTemplatePayloadFromUiInput,
  mapRawWhatsAppTemplateToUiTemplate,
} from "./whatsappTemplateTransformers";
import { uploadWhatsAppTemplateHeaderMedia } from "./whatsappTemplateMediaUpload";
import { uploadWhatsAppMessageMedia } from "./uploadWhatsAppMessageMedia";
import { upsertWhatsAppTemplateMediaBasic } from "./whatsappTemplateMediaBasicService";

export interface ListMessageTemplatesParams {
  whatsappBusinessAccountId: string;
}

export interface CreateMessageTemplateParams {
  whatsappBusinessAccountId: string;
  phoneNumberId?: string | null;
  organizationId?: string | null;
  input: CreateMessageTemplateInput;
}

export interface UpdateMessageTemplateParams {
  whatsappBusinessAccountId: string;
  phoneNumberId?: string | null;
  organizationId?: string | null;
  input: MessageTemplate & {
    headerMediaFile?: File | null;
  };
}

export interface RemoveMessageTemplateParams {
  whatsappBusinessAccountId: string;
  /**
   * O endpoint de DELETE usado no legado funciona via `?name=...`.
   * Na UI, o usuário manipula pelo `id`, então quem resolve o `name`
   * é o hook (`useMessageTemplates`).
   */
  templateName: string;
}

const REQUEST_TIMEOUT_MS = 30000;
const MESSAGE_TEMPLATES_PAGE_LIMIT = 100;
const MAX_MESSAGE_TEMPLATES_PAGES = 20;

const DEFAULT_PHONE_NUMBER_ID =
  String(import.meta.env.VITE_FLUXZAP_DEFAULT_PHONE_NUMBER_ID ?? "").trim() ||
  "379190331933594";

function resolvePhoneNumberIdForMedia(
  phoneNumberId?: string | null,
): string {
  return String(phoneNumberId ?? "").trim() || DEFAULT_PHONE_NUMBER_ID;
}

function buildMessageTemplatesBaseUrl(params: {
  whatsappBusinessAccountId: string;
}): string {
  return `${endpoints.XDSB_domain}/whatsappbusiness/${params.whatsappBusinessAccountId}/message_templates`;
}

function buildUpdateUrl(templateId: string): string {
  return `${endpoints.XDSB_domain}/whatsappbusiness/${templateId}`;
}

function buildMessageTemplatesListUrl(
  baseUrl: string,
  params?: { after?: string; limit?: number },
): string {
  const url = new URL(baseUrl);
  url.searchParams.set("limit", String(params?.limit ?? MESSAGE_TEMPLATES_PAGE_LIMIT));
  if (params?.after?.trim()) {
    url.searchParams.set("after", params.after.trim());
  }
  return url.toString();
}

function extractTemplatesFromResponse(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;

  const envelope = raw as Record<string, unknown>;
  const data = envelope?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(envelope?.templates)) {
    return envelope.templates as unknown[];
  }

  if (data && typeof data === "object") {
    const nested = data as Record<string, unknown>;
    if (Array.isArray(nested.data)) return nested.data;
    if (Array.isArray(nested.templates)) return nested.templates;
  }

  return [];
}

function extractPagingEnvelope(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== "object") return null;
  const envelope = raw as Record<string, unknown>;
  const nestedData = envelope.data;
  if (nestedData && typeof nestedData === "object" && !Array.isArray(nestedData)) {
    const nestedPaging = (nestedData as Record<string, unknown>).paging;
    if (nestedPaging && typeof nestedPaging === "object") {
      return nestedPaging as Record<string, unknown>;
    }
  }
  const paging = envelope.paging;
  if (paging && typeof paging === "object") {
    return paging as Record<string, unknown>;
  }
  return null;
}

function extractAfterCursor(raw: unknown): string | null {
  const paging = extractPagingEnvelope(raw);
  if (!paging) return null;

  const cursors = paging.cursors;
  if (cursors && typeof cursors === "object") {
    const after = (cursors as Record<string, unknown>).after;
    if (typeof after === "string" && after.trim()) {
      return after.trim();
    }
  }

  const next = paging.next;
  if (typeof next === "string" && next.trim()) {
    try {
      const afterFromNext = new URL(next).searchParams.get("after");
      if (afterFromNext?.trim()) return afterFromNext.trim();
    } catch {
      // `paging.next` externo: não seguimos a URL no browser.
    }
  }

  return null;
}

async function fetchAllRawMessageTemplates(
  baseUrl: string,
): Promise<unknown[]> {
  const collected: unknown[] = [];
  let afterCursor: string | null = null;
  let pageIndex = 0;

  do {
    const pageUrl = buildMessageTemplatesListUrl(baseUrl, {
      after: afterCursor ?? undefined,
    });
    const page = await httpGet<unknown>(pageUrl, {
      timeout: REQUEST_TIMEOUT_MS,
    });
    const pageTemplates = extractTemplatesFromResponse(page);
    collected.push(...pageTemplates);

    afterCursor = extractAfterCursor(page);
    pageIndex += 1;

    if (!afterCursor || pageTemplates.length === 0) {
      break;
    }
  } while (pageIndex < MAX_MESSAGE_TEMPLATES_PAGES);

  return collected;
}

function isMediaHeaderFormat(
  format: CreateMessageTemplateInput["headerFormat"] | undefined,
): format is "IMAGE" | "VIDEO" | "DOCUMENT" {
  return format === "IMAGE" || format === "VIDEO" || format === "DOCUMENT";
}

async function resolveHeaderMediaHandle(params: {
  input: CreateMessageTemplateInput | (MessageTemplate & { headerMediaFile?: File | null });
}): Promise<string | undefined> {
  if (!isMediaHeaderFormat(params.input.headerFormat)) {
    return undefined;
  }

  const file =
    "headerMediaFile" in params.input ? params.input.headerMediaFile : null;

  if (file) {
    return uploadWhatsAppTemplateHeaderMedia({ file });
  }

  const existing = String(params.input.headerMediaHandle ?? "").trim();
  if (existing) return existing;

  throw new Error(
    "Selecione um arquivo de amostra para o cabeçalho de mídia.",
  );
}

async function persistMessageMediaId(params: {
  file: File | null;
  phoneNumberId?: string | null;
  organizationId?: string | null;
  templateName: string;
}): Promise<void> {
  if (!params.file) return;

  const phoneNumberId = resolvePhoneNumberIdForMedia(params.phoneNumberId);

  const organizationId = String(params.organizationId ?? "").trim();
  if (!organizationId) {
    throw new Error("Organização é obrigatória para salvar o id de mídia.");
  }

  const mediaId = await uploadWhatsAppMessageMedia({
    phoneNumberId,
    file: params.file,
  });

  await upsertWhatsAppTemplateMediaBasic({
    organizationId,
    templateName: params.templateName,
    mediaId,
  });
}

async function list(params: ListMessageTemplatesParams): Promise<MessageTemplate[]> {
  const baseUrl = buildMessageTemplatesBaseUrl(params);
  const allRawTemplates = await fetchAllRawMessageTemplates(baseUrl);

  return allRawTemplates
    .map((raw: unknown) =>
      mapRawWhatsAppTemplateToUiTemplate(raw as Record<string, unknown>),
    )
    .filter((template: MessageTemplate) =>
      Boolean(String(template.name ?? "").trim()),
    );
}

async function create(params: CreateMessageTemplateParams): Promise<void> {
  const baseUrl = buildMessageTemplatesBaseUrl(params);
  const resolvedHeaderMediaHandle = await resolveHeaderMediaHandle({
    input: params.input,
  });
  const payload = buildWhatsAppTemplatePayloadFromUiInput(params.input, {
    resolvedHeaderMediaHandle,
  });
  await httpPost<unknown>(baseUrl, payload, { timeout: REQUEST_TIMEOUT_MS });

  const file =
    "headerMediaFile" in params.input ? params.input.headerMediaFile ?? null : null;
  await persistMessageMediaId({
    file,
    phoneNumberId: params.phoneNumberId,
    organizationId: params.organizationId,
    templateName: payload.name,
  });
}

async function update(params: UpdateMessageTemplateParams): Promise<void> {
  const updateUrl = buildUpdateUrl(params.input.id);
  const resolvedHeaderMediaHandle = await resolveHeaderMediaHandle({
    input: params.input,
  });
  const payload = buildWhatsAppTemplatePayloadFromUiInput(params.input, {
    resolvedHeaderMediaHandle,
  });
  await httpPost<unknown>(updateUrl, payload, { timeout: REQUEST_TIMEOUT_MS });

  const file =
    "headerMediaFile" in params.input ? params.input.headerMediaFile ?? null : null;
  await persistMessageMediaId({
    file,
    phoneNumberId: params.phoneNumberId,
    organizationId: params.organizationId,
    templateName: payload.name,
  });
}

async function remove(params: RemoveMessageTemplateParams): Promise<void> {
  const baseUrl = buildMessageTemplatesBaseUrl(params);
  const url = `${baseUrl}?name=${encodeURIComponent(params.templateName)}`;
  await httpDelete<unknown>(url, { timeout: REQUEST_TIMEOUT_MS });
}

export const messageTemplatesService = {
  list,
  create,
  update,
  remove,
};
